/**
 * Utility functions for demand-charge data extracted from the daily-summary CSV.
 *
 * The daily-summary CSV has a two-row header. After the standard parser merges
 * them the relevant columns become "Demand Peak Power (kW)" and
 * "Demand Demand Charge ($)".  Dates arrive as M/D/YY and must be normalised to
 * YYYY-MM-DD so they can be filtered with the same logic the overlay chart uses.
 *
 * The sim also splits the monthly demand charge across the three cost
 * categories, on the same row as the charge itself: "Demand Production Demand
 * ($)", "Demand Consumption Demand ($)", "Demand Storage Demand ($)" and
 * "Demand Unattributed Demand ($)". They are signed the way every other money
 * column in the daily summary is - a cost negative, a benefit positive - so
 * building load and storage charging read negative while solar and storage
 * discharge, which pull the peak down, read positive. The four sum to minus the
 * billed "Demand Charge ($)", which is stored as a positive magnitude.
 *
 * Results produced before the sim wrote those columns have none of them. Those
 * runs report absence rather than 0, because a 0 would read as "this category
 * had nothing to do with the peak" when in truth it was never measured.
 */

const PEAK_POWER_CANDIDATES = ["Peak Power (kW)", "Demand Peak Power (kW)"];
const DEMAND_CHARGE_CANDIDATES = [
  "Demand Charge ($)",
  "Demand Demand Charge ($)",
];

/* The attribution columns, keyed by the field they become on each row. The
 * lookup is a substring match, so the bare column name also matches the
 * "Demand "-prefixed form the two-row header merge produces. */
const ATTRIBUTION_COLUMNS = {
  productionDemand: "Production Demand ($)",
  consumptionDemand: "Consumption Demand ($)",
  storageDemand: "Storage Demand ($)",
  unattributedDemand: "Unattributed Demand ($)",
};

export const ATTRIBUTION_FIELDS = Object.keys(ATTRIBUTION_COLUMNS);

/* Per-DEVICE demand attribution, alongside the per-category split above.
 *
 * The sim writes one "<device> Demand Attribution ($)" column per production
 * and storage device. After the two-row header merge these arrive as
 * "Battery1 Demand Attribution ($)", "11 Top Picks Day A Demand
 * Attribution ($)" and so on, so the device name is the part before the
 * suffix.
 *
 * DAILY SUMMARY ONLY. The timeseries carries no such column, which is why this
 * lives here and not in metrics.worker.js with the other per-device figures.
 *
 * Verified on husky-2040-prod stamp 082926_0219 over the full year: the eight
 * per-device values sum to -876,447.26, which is exactly the category split
 * Production Demand 2,719.57 + Storage Demand -879,166.83. Consumption Demand
 * has no per-device counterpart because building load is the meter itself
 * rather than a device. */
const DEVICE_ATTRIBUTION_SUFFIX = " Demand Attribution ($)";

/** Device names that carry a per-device attribution column in these rows. */
export function deviceAttributionNames(parsedRows) {
  if (!Array.isArray(parsedRows) || !parsedRows.length) return [];
  const limit = Math.min(parsedRows.length, KEY_PROBE_ROWS);
  const names = new Set();
  for (let i = 0; i < limit; i++) {
    const row = parsedRows[i];
    if (!row || typeof row !== "object") continue;
    for (const key of Object.keys(row)) {
      if (key.endsWith(DEVICE_ATTRIBUTION_SUFFIX)) {
        const name = key.slice(0, -DEVICE_ATTRIBUTION_SUFFIX.length).trim();
        if (name) names.add(name);
      }
    }
  }
  return [...names];
}

/**
 * Per-device attribution for ONE parsed row, keyed by device name.
 *
 * Only the devices whose column is actually present on the row are keyed, so a
 * device missing from this run stays missing rather than arriving as a 0.
 */
function readDeviceAttribution(row, deviceNames) {
  const values = {};
  for (const name of deviceNames) {
    const column = name + DEVICE_ATTRIBUTION_SUFFIX;
    if (row && typeof row === "object" && column in row) {
      values[name] = row[column];
    }
  }
  return values;
}

/**
 * Sum one device's demand attribution over rows already filtered to a period.
 *
 * Reads either shape:
 *   - a RAW parsed daily-summary row, keyed "Battery1 Demand Attribution ($)"
 *   - a row projected by `extractDemandChargeRows`, which carries the same
 *     figures under `deviceAttribution` keyed by bare device name
 *
 * Both because the projection is what the hook publishes and what every period
 * filter downstream slices, while the raw shape is what a caller holding the
 * parser's output already has. One reader for both keeps the two from drifting
 * on what an absent device means.
 *
 * Returns null when the run carries no column for that device -- absent, not
 * zero, so the card renders an em-dash rather than claiming the device had
 * nothing to do with the peak.
 */
export function sumDeviceAttribution(filteredRows, deviceName) {
  if (!Array.isArray(filteredRows) || !filteredRows.length || !deviceName) {
    return null;
  }
  const column = deviceName + DEVICE_ATTRIBUTION_SUFFIX;
  let total = 0;
  let seen = false;
  for (const row of filteredRows) {
    if (!row || typeof row !== "object") continue;

    let raw;
    if (column in row) {
      raw = row[column];
    } else if (
      row.deviceAttribution &&
      typeof row.deviceAttribution === "object" &&
      deviceName in row.deviceAttribution
    ) {
      raw = row.deviceAttribution[deviceName];
    } else {
      continue;
    }

    seen = true;
    const n = parseFloat(raw);
    if (Number.isFinite(n)) total += n;
  }
  return seen ? total : null;
}

/* Probe several rows for a column, not just row 0: a single ragged row would
 * otherwise disable a column for the whole run. */
const KEY_PROBE_ROWS = 50;

function findKey(row, candidate) {
  if (!row || typeof row !== "object") return null;
  return Object.keys(row).find((k) => k.includes(candidate)) ?? null;
}

function fuzzyGet(row, candidates) {
  for (const c of candidates) {
    const found = findKey(row, c);
    if (found != null) return parseFloat(row[found]) || 0;
  }
  return 0;
}

/**
 * Whether the parsed rows carry all four attribution columns.
 *
 * All four or none: the sim writes them as a set, and a partial set would mean
 * the four no longer sum to the bill, which is the only reason to show them.
 */
export function hasAttributionColumns(parsedRows) {
  if (!Array.isArray(parsedRows) || !parsedRows.length) return false;
  const limit = Math.min(parsedRows.length, KEY_PROBE_ROWS);
  return Object.values(ATTRIBUTION_COLUMNS).every((column) => {
    for (let i = 0; i < limit; i++) {
      if (findKey(parsedRows[i], column) != null) return true;
    }
    return false;
  });
}

/**
 * Whether the parsed rows carry the charge columns at all.
 *
 * `fuzzyGet` returns 0 on a miss, so without this probe a run whose daily
 * summary never had "Demand Charge ($)" is indistinguishable from one billed
 * $0.00 - and the card, told by the meter's sysconfig that a demand charge
 * exists, renders that fabricated 0 on its largest money row. Same shape as
 * hasAttributionColumns above: measured or absent, never a seeded zero.
 *
 * Both columns or neither, for the same reason the attribution set is all four:
 * the sim writes the peak and the charge it produced together, and a lone half
 * would leave the card showing a bill with no peak behind it.
 */
export function hasChargeColumns(parsedRows) {
  if (!Array.isArray(parsedRows) || !parsedRows.length) return false;
  const limit = Math.min(parsedRows.length, KEY_PROBE_ROWS);
  return [DEMAND_CHARGE_CANDIDATES, PEAK_POWER_CANDIDATES].every(
    (candidates) => {
      for (let i = 0; i < limit; i++) {
        for (const candidate of candidates) {
          if (findKey(parsedRows[i], candidate) != null) return true;
        }
      }
      return false;
    },
  );
}

/**
 * Normalise M/D/YY (or M/D/YYYY) to YYYY-MM-DD.
 * Assumes years 00-99 map to 2000-2099.
 */
export function normaliseDateMDY(raw) {
  if (!raw || typeof raw !== "string") return "";

  /* A daily-summary stamp may carry a time ("1/1/25 0:00"). Splitting the whole
   * string on "/" put that time inside the year field and yielded
   * "2024 0:00-01-01", so the date is taken off the front first.
   *
   * The 2-digit year window below is a local policy for these CSVs and is
   * deliberately NOT shared with utils/simulationDate.js, which rejects 2-digit
   * years as unresolvably ambiguous. Consolidating the two would have to pick
   * one policy silently; keeping them apart leaves the choice visible here. */
  const [datePart] = raw.trim().split(/[ T]/, 1);
  const parts = datePart.split("/");
  if (parts.length !== 3) return raw;

  let [month, day, year] = parts;
  if (year.length <= 2) year = `20${year.padStart(2, "0")}`;
  return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
}

/**
 * Transform parsed daily-summary rows into a uniform array:
 *   [{ date: "YYYY-MM-DD", peakPowerKw, demandCharge, hasCharge,
 *      hasAttribution,
 *      productionDemand, consumptionDemand, storageDemand,
 *      unattributedDemand,
 *      deviceAttribution: { "Battery1": "334627.12", ... } | null }]
 *
 * When the run predates the attribution columns the four values are null and
 * `hasAttribution` is false - never a fabricated 0. `hasCharge` says the same
 * thing about the charge and peak themselves.
 *
 * ── Why the per-device figures ride ON the projected row ──────────────────
 * The alternative was to have the hook publish the raw pre-projection rows
 * beside `dailyDemand`. That was rejected: a raw row is ~130 CSV columns keyed
 * off a merged two-row header and carries the date as the locale string the
 * file happened to use ("01/01/2040"), so every consumer wanting a period
 * slice would have to re-run `normaliseDateMDY` for itself -- reverse-parsing
 * the very thing this projection exists to normalise -- and two arrays that
 * must stay index-aligned are two arrays that can fall out of alignment.
 *
 * Carried here instead, there is ONE array, normalised once, sliced by ONE
 * predicate (see `filterRowsForPeriod`), and the per-device figures are
 * automatically consistent with the per-category ones summed beside them.
 *
 * The values are passed through as the parser produced them rather than being
 * coerced: a device whose column is missing from a row is simply not keyed, so
 * absence survives to the card instead of arriving as 0.
 */
export function extractDemandChargeRows(parsedRows) {
  if (!Array.isArray(parsedRows)) return [];
  const hasAttribution = hasAttributionColumns(parsedRows);
  const hasCharge = hasChargeColumns(parsedRows);
  /* Probed once over the run, not per row: the column set is a property of the
   * file's header, and probing per row would be O(rows x columns). */
  const deviceNames = deviceAttributionNames(parsedRows);
  return parsedRows
    .map((row) => {
      const rawDate = row.Date || row.date || "";
      const date = normaliseDateMDY(rawDate);
      if (!date) return null;
      const extracted = {
        date,
        peakPowerKw: fuzzyGet(row, PEAK_POWER_CANDIDATES),
        demandCharge: fuzzyGet(row, DEMAND_CHARGE_CANDIDATES),
        hasCharge,
        hasAttribution,
        /* null, not {}, when the run carries no per-device columns at all:
         * an empty object would read as "measured, and every device was
         * nothing". */
        deviceAttribution: deviceNames.length
          ? readDeviceAttribution(row, deviceNames)
          : null,
      };
      for (const field of ATTRIBUTION_FIELDS) {
        extracted[field] = hasAttribution
          ? fuzzyGet(row, [ATTRIBUTION_COLUMNS[field]])
          : null;
      }
      return extracted;
    })
    .filter(Boolean);
}

/* The period views a caller can ask for. Anything else means "the whole run",
 * which is what the all-time view and an unset view have always meant here. */
const PERIOD_VIEWS = ["daily", "monthly", "yearly", "all time view"];

/**
 * The ONE period predicate. Every figure sliced out of `dailyDemand` -- the
 * billed charge, the four category attributions, a single device's share --
 * goes through this, so no two of them can disagree about which days a month
 * contains.
 *
 *   daily   -> date.startsWith(selDate)          "YYYY-MM-DD"
 *   monthly -> date.slice(0,7) === selMonth      "YYYY-MM"
 *   yearly  -> date.slice(0,4) === selYear       "YYYY"
 *   anything else (all time view) -> every row
 *
 * The keys are ISO prefixes, never a formatted display string: "January 2040"
 * cannot be compared against a date and reverse-parsing it back into "2040-01"
 * is a locale-dependent guess.
 */
export function filterRowsForPeriod(rows, view, selDate, selMonth, selYear) {
  if (!Array.isArray(rows)) return [];
  if (view === "daily") return rows.filter((r) => r.date.startsWith(selDate));
  if (view === "monthly")
    return rows.filter((r) => r.date.slice(0, 7) === selMonth);
  if (view === "yearly")
    return rows.filter((r) => r.date.slice(0, 4) === selYear);
  return rows;
}

/**
 * One device's demand attribution over the selected period.
 *
 * The per-device counterpart of `sumDemandChargeForPeriod`, sharing its
 * predicate exactly, so a device card and the summary card above it are always
 * describing the same days.
 *
 * Returns null -- absent, never 0 -- for every reason a figure might not exist:
 * no rows, no device, no column for that device in this run, or a period the
 * caller has not resolved yet. That last one matters on a device page: the
 * chart's selectors are filled by an effect, so for one render after a view
 * switch the new view's key is still "". Falling through to "every row" there
 * would put the WHOLE RUN's attribution on a card labelled with a single day,
 * which is worse than an em-dash because it looks right.
 */
export function sumDeviceAttributionForPeriod(
  dailyDemand,
  deviceName,
  view,
  selDate,
  selMonth,
  selYear,
) {
  if (!Array.isArray(dailyDemand) || !dailyDemand.length || !deviceName) {
    return null;
  }
  if (!PERIOD_VIEWS.includes(view)) return null;
  if (view === "daily" && !selDate) return null;
  if (view === "monthly" && !selMonth) return null;
  if (view === "yearly" && !selYear) return null;

  return sumDeviceAttribution(
    filterRowsForPeriod(dailyDemand, view, selDate, selMonth, selYear),
    deviceName,
  );
}

/**
 * Sum `demandCharge` values over the rows that match the overlay period filter.
 *
 * Mirrors the period-slicing logic in OverlayChart:
 *   daily   -> date.startsWith(selDate)          "YYYY-MM-DD"
 *   monthly -> date.slice(0,7) === selMonth      "YYYY-MM"
 *   yearly  -> date.slice(0,4) === selYear       "YYYY"
 *   all time view -> all rows
 *
 * The per-category attribution is summed over the same slice, so the four
 * category totals still sum to minus the period's total charge. When the rows
 * carry no attribution the four come back null and `hasAttribution` is false.
 * `hasCharge` travels the same way for the charge and peak, so a caller can
 * tell a measured $0.00 from a run that never carried the column.
 */
export function sumDemandChargeForPeriod(
  dailyDemand,
  view,
  selDate,
  selMonth,
  selYear,
) {
  const absent = {
    totalCharge: 0,
    maxPeakPower: 0,
    hasCharge: false,
    hasAttribution: false,
    productionDemand: null,
    consumptionDemand: null,
    storageDemand: null,
    unattributedDemand: null,
  };
  if (!Array.isArray(dailyDemand) || !dailyDemand.length) return absent;

  const filtered = filterRowsForPeriod(
    dailyDemand,
    view,
    selDate,
    selMonth,
    selYear,
  );

  /* One row without the columns disqualifies the whole slice: a partial sum
   * would silently understate the categories against the total. */
  const hasAttribution =
    filtered.length > 0 && filtered.every((r) => r.hasAttribution);
  /* Same all-or-nothing rule as the attribution: one row of the slice without
   * the column would make the sum a subtotal presented as the period's bill. */
  const hasCharge = filtered.length > 0 && filtered.every((r) => r.hasCharge);

  let totalCharge = 0;
  let maxPeakPower = 0;
  const attribution = Object.fromEntries(
    ATTRIBUTION_FIELDS.map((field) => [field, 0]),
  );
  for (const row of filtered) {
    totalCharge += row.demandCharge;
    if (row.peakPowerKw > maxPeakPower) maxPeakPower = row.peakPowerKw;
    if (hasAttribution) {
      for (const field of ATTRIBUTION_FIELDS) {
        attribution[field] += row[field] || 0;
      }
    }
  }

  return {
    totalCharge,
    maxPeakPower,
    hasCharge,
    hasAttribution,
    ...(hasAttribution
      ? attribution
      : Object.fromEntries(ATTRIBUTION_FIELDS.map((field) => [field, null]))),
  };
}

/**
 * The demand-charge figures a CARD reads, for one period.
 *
 * ── Why this is shared rather than inlined per surface ────────────────────
 * The Summary tab built this payload inside OverlayChart, and the three device
 * pages had nothing at all. Wiring each page to build its own would have put
 * four copies of the same absence rules -- which zero is measured, which is
 * fabricated, when the four category values become null -- in four files, and
 * the Consumption device card in particular has to show the SAME figure as the
 * Summary Consumption card for the same period. Two hand-written copies of that
 * is a guarantee they eventually disagree; one function is a guarantee they
 * cannot.
 *
 * Returns the shape `cardFlowRows` reads: `hasDemandCharge` from the meter's
 * sysconfig (the site is BILLED a demand charge) and the figures from the daily
 * summary, carried as null rather than 0 wherever the run did not record them.
 * Money is stringified at two decimals here, which is the single rounding path
 * every card total and every demand row goes through.
 *
 * Callers add their own `view` / `selectedPeriod` display stamps on top; those
 * are labels, not figures, and are deliberately not this function's business.
 */
export function buildDemandChargePayload(
  hasDemandCharge,
  dailyDemand,
  view,
  selDate,
  selMonth,
  selYear,
) {
  if (!hasDemandCharge || !dailyDemand?.length) {
    return {
      value: "0.00",
      peakPower: "0.00",
      hasDemandCharge: false,
      hasAttribution: false,
      productionDemand: null,
      consumptionDemand: null,
      storageDemand: null,
      unattributedDemand: null,
    };
  }

  const {
    totalCharge,
    maxPeakPower,
    hasCharge,
    hasAttribution,
    productionDemand,
    consumptionDemand,
    storageDemand,
    unattributedDemand,
  } = sumDemandChargeForPeriod(dailyDemand, view, selDate, selMonth, selYear);

  return {
    /* `hasDemandCharge` comes from the meter's sysconfig - the site is BILLED a
     * demand charge - while the figure comes from the daily summary. A run
     * written before the sim reported it has neither column, and the summing
     * helper's 0 would publish as a measured "0.00" on the card's largest money
     * row. Carry the absence instead: hasCardValue(null) is false downstream
     * and the row shows an em-dash. */
    value: hasCharge ? totalCharge.toFixed(2) : null,
    peakPower: hasCharge ? maxPeakPower.toFixed(2) : null,
    hasDemandCharge: true,
    /* Runs predating the sim's demand attribution have no per-category columns.
     * Carry the absence through as null so the Production, Consumption and
     * Storage cards can show an em-dash instead of a 0 that would read as "this
     * category did not touch the peak". */
    hasAttribution,
    productionDemand: hasAttribution ? productionDemand.toFixed(2) : null,
    consumptionDemand: hasAttribution ? consumptionDemand.toFixed(2) : null,
    storageDemand: hasAttribution ? storageDemand.toFixed(2) : null,
    unattributedDemand: hasAttribution ? unattributedDemand.toFixed(2) : null,
  };
}

/**
 * Check whether demand_charge is configured on a meter.
 */
export function hasDemandChargeConfig(meter) {
  return Boolean(
    meter?.demand_charge &&
      typeof meter.demand_charge === "object" &&
      meter.demand_charge.type &&
      meter.demand_charge.type !== "none",
  );
}
